import { isNumber, isPlainObject, isString, sort } from 'remeda'
import type { Cleanup } from '../helpers/cleanup'
import { clientEventDetail } from './combobox/utils'

// Private types ---------------------------------------------------------------

type ColumnOptionsView = {
  to_columns(): Promise<Record<string, unknown>>
  delete(): Promise<void>
}

// Public types ----------------------------------------------------------------

export type ColumnOptionsTable = {
  view(config: Record<string, unknown>): Promise<ColumnOptionsView>
}

export type ColumnOptionsRequest = Readonly<{
  field: string
  filter: string
  page: number
  pageSize: number
}>

export type ColumnOptionsPage = Readonly<{
  result: readonly Readonly<{ label: string; value: string }>[]
  size: number
}>

// Constants -------------------------------------------------------------------

// Dispatched by comboboxes rendered with `data-client-options` for report filters.
export const REPORT_COLUMN_OPTIONS_EVENT = '__report_column_options__'

// Natural order for display values: "Склад 2" before "Склад 10".
const VALUE_COLLATOR = new Intl.Collator(undefined, { numeric: true })

// Private functions -----------------------------------------------------------

// Option pages are zero-based and must be safe JSON integers.
const isPageNumber = (value: unknown): value is number =>
  isNumber(value) && Number.isSafeInteger(value) && value >= 0

// Validates the combobox option-page payload once at the event boundary.
const parseRequest = (payload: unknown): ColumnOptionsRequest | undefined =>
  isPlainObject(payload) &&
  isString(payload.filter) &&
  isPageNumber(payload.page) &&
  isPageNumber(payload.pageSize) &&
  isPlainObject(payload.params) &&
  isString(payload.params.field)
    ? {
        field: payload.params.field,
        filter: payload.filter,
        page: payload.page,
        pageSize: payload.pageSize
      }
    : undefined

// Group-by row paths are `[]` for the total row and `[value]` for each group.
const groupValues = (rowPaths: unknown): readonly string[] =>
  Array.isArray(rowPaths)
    ? rowPaths.flatMap((path: unknown) =>
        Array.isArray(path) && path.length === 1 && isString(path[0])
          ? [path[0]]
          : []
      )
    : []

// Reads the distinct values of one column from the loaded report rows.
const loadColumnValues = async (
  table: ColumnOptionsTable,
  field: string
): Promise<readonly string[]> => {
  const view = await table.view({ group_by: [field], columns: [] })

  try {
    return groupValues((await view.to_columns()).__ROW_PATH__)
  } finally {
    await view.delete()
  }
}

// Public functions ------------------------------------------------------------

// Pages the distinct values matching a case-insensitive search as label=value options.
export const columnOptionsPage = (
  values: readonly string[],
  request: ColumnOptionsRequest
): ColumnOptionsPage => {
  const query = request.filter.trim().toLocaleLowerCase()
  const matches = sort(
    values.filter(
      (value) => value !== '' && value.toLocaleLowerCase().includes(query)
    ),
    VALUE_COLLATOR.compare
  )
  const start = request.page * request.pageSize

  return {
    result: matches
      .slice(start, start + request.pageSize)
      .map((value) => ({ label: value, value })),
    size: matches.length
  }
}

// Answers report filter comboboxes from the table this report has loaded.
export const listenColumnOptions = (
  getTable: () => ColumnOptionsTable | undefined
): Cleanup => {
  const handle = (event: Event): void => {
    const detail = clientEventDetail(event)

    if (!detail) {
      return
    }

    const request = parseRequest(detail.payload)
    const table = getTable()

    // Replying `undefined` fails the combobox fast instead of waiting for its timeout.
    if (!(request && table)) {
      detail.reply(undefined)
      return
    }

    loadColumnValues(table, request.field).then(
      (values) => detail.reply(columnOptionsPage(values, request)),
      () => detail.reply(undefined)
    )
  }

  document.addEventListener(REPORT_COLUMN_OPTIONS_EVENT, handle)

  return () => document.removeEventListener(REPORT_COLUMN_OPTIONS_EVENT, handle)
}
